import { useState } from "react";
import axios from "axios";
import { Loader2, PackageSearch, CheckCircle2, Clock, XCircle, Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const STATUS_ICON = {
  paid: <CheckCircle2 size={20} strokeWidth={1.5} className="text-[#BA8D53]" />,
  created: <Clock size={20} strokeWidth={1.5} className="text-black/40" />,
  failed: <XCircle size={20} strokeWidth={1.5} className="text-red-700" />,
};

const STAGES = ["processing", "shipped", "delivered"];
const STAGE_TITLES = { processing: "Confirmed", shipped: "Shipped", delivered: "Delivered" };

const StageProgress = ({ stage }) => {
  const activeIdx = STAGES.indexOf(stage);
  if (activeIdx === -1) return null;
  return (
    <div className="mt-7 flex items-center" data-testid="track-stage-progress">
      {STAGES.map((s, i) => (
        <div key={s} className={`flex items-center ${i > 0 ? "flex-1" : ""}`}>
          {i > 0 && <div className={`h-px flex-1 mx-2 ${i <= activeIdx ? "bg-[#BA8D53]" : "bg-black/15"}`} />}
          <div className="flex flex-col items-center gap-2">
            <div
              className={`h-3 w-3 rounded-full border ${
                i <= activeIdx ? "bg-[#BA8D53] border-[#BA8D53]" : "bg-transparent border-black/25"
              }`}
            />
            <span className={`text-[0.6rem] uppercase tracking-[0.18em] ${i <= activeIdx ? "text-[#111]" : "text-black/35"}`}>
              {STAGE_TITLES[s]}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const ReviewForm = ({ orderId, email, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Please select a star rating.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, { order_id: orderId, email, rating, text });
      toast.success("Thank you — your review is now live on Word of Mouth.");
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="review-form" className="mt-7 pt-6 border-t border-black/10">
      <p className="overline-label">Share your experience</p>
      <p className="mt-2 text-xs text-black/50">How is your fragrance? Your review appears on our Word of Mouth page.</p>
      <div className="mt-4 flex gap-1.5" data-testid="review-star-rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`review-star-${n}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(n)}
            className="text-[#BA8D53] transition-transform hover:scale-110"
          >
            <Star size={22} strokeWidth={1.2} fill={(hover || rating) >= n ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
      <textarea
        required
        minLength={10}
        maxLength={1000}
        rows={4}
        data-testid="review-text-input"
        className="input-minimal mt-5 resize-none w-full"
        placeholder="Tell us about the scent, the longevity, the compliments…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <button type="submit" disabled={sending} data-testid="review-submit-button" className="btn-ink mt-6 disabled:opacity-50">
        {sending ? <Loader2 size={14} className="animate-spin" /> : null}
        {sending ? "Submitting…" : "Submit Review"}
      </button>
    </form>
  );
};

export default function TrackOrder() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const track = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/orders/track`, { order_id: orderId.trim(), email: email.trim() });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not find your order. Please check the details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="track-order-page" className="pt-24 md:pt-32 min-h-screen">
      <Seo
        title="Track Your Order — Reserve 30 Parfums"
        description="Check the status of your Reserve 30 Parfums order using your email and order ID."
      />
      <div className="max-w-2xl mx-auto px-5 pb-28">
        <Reveal>
          <div className="flex items-center gap-3">
            <PackageSearch size={18} strokeWidth={1.5} className="text-[#BA8D53]" />
            <p className="overline-label">Order Status</p>
          </div>
          <h1 className="font-serif-brand text-5xl md:text-6xl font-light mt-4">Track your order.</h1>
          <p className="mt-5 text-sm text-black/55 leading-relaxed max-w-md">
            Enter the email used at checkout and your Order ID — you'll find it on the order confirmation screen and in your confirmation email.
          </p>
        </Reveal>

        <form onSubmit={track} data-testid="track-order-form" className="mt-12 space-y-8">
          <input
            required
            type="email"
            data-testid="track-email-input"
            className="input-minimal"
            placeholder="Email used at checkout"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            required
            minLength={8}
            data-testid="track-order-id-input"
            className="input-minimal"
            placeholder="Order ID (e.g. 4F2A91BC)"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
          />
          {error && <p data-testid="track-order-error" className="text-sm text-red-700">{error}</p>}
          <button type="submit" disabled={loading} data-testid="track-order-submit" className="btn-ink disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : null}
            {loading ? "Searching…" : "Track Order"}
          </button>
        </form>

        {result && (
          <Reveal className="mt-14">
            <div data-testid="track-order-result" className="border border-black/10 bg-white/40 p-7 md:p-9">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[0.65rem] uppercase tracking-[0.24em] text-black/40">Order {result.order_id}</p>
                <div className="flex items-center gap-2">
                  {STATUS_ICON[result.raw_status]}
                  <span data-testid="track-order-status" className="text-sm font-medium">{result.status}</span>
                </div>
              </div>
              <p className="mt-2 text-xs text-black/40">
                Placed {new Date(result.created_at).toLocaleString("en-IN")}
                {result.paid_at ? ` · Paid ${new Date(result.paid_at).toLocaleString("en-IN")}` : ""}
              </p>
              {result.raw_status === "paid" && <StageProgress stage={result.stage} />}
              <div className="mt-6 pt-5 border-t border-black/10 space-y-2 text-sm text-black/70">
                {result.items.map((i) => (
                  <p key={`${i.name}-${i.variant_label}`}>{i.name} — {i.variant_label} × {i.qty}</p>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-black/10 space-y-2 text-sm">
                <div className="flex justify-between text-black/60"><span>Subtotal</span><span>{inr(result.subtotal)}</span></div>
                {result.discount_amount > 0 && (
                  <div className="flex justify-between text-[#BA8D53]"><span>Discount {result.discount_code ? `(${result.discount_code})` : ""}</span><span>−{inr(result.discount_amount)}</span></div>
                )}
                <div className="flex justify-between text-black/60"><span>Shipping</span><span>{result.shipping === 0 ? "Free" : inr(result.shipping)}</span></div>
                <div className="flex justify-between font-medium text-base pt-2 border-t border-black/10"><span>Total</span><span>{inr(result.total)}</span></div>
              </div>
              {result.raw_status === "paid" && (
                <p className="mt-6 text-xs text-black/50 leading-relaxed">
                  Questions? Write to contact@reserve30.in with your Order ID.
                </p>
              )}
              {result.raw_status === "paid" && !result.review_submitted && (
                <ReviewForm
                  orderId={orderId.trim()}
                  email={email.trim()}
                  onSubmitted={() => setResult({ ...result, review_submitted: true })}
                />
              )}
              {result.raw_status === "paid" && result.review_submitted && (
                <p data-testid="review-thanks" className="mt-7 pt-6 border-t border-black/10 text-sm text-[#BA8D53]">
                  Thank you — your review for this order has been received.
                </p>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
